type EventHandlerFn = (values: object) => void
type EventHandlerEntry = { event_name: string; handler_fn: EventHandlerFn }

/**
 * Event emitter with typed events
 */
class TypedEventEmitter<TEvents extends Record<string, object>> {
	#handlers: EventHandlerEntry[] = []

	#_emit(event_name: string, values: object) {
		for (const handler of this.#handlers) {
			if (handler.event_name === event_name) {
				handler.handler_fn(values)
			}
		}
	}

	#_subscribe(event_name: string, handler_fn: EventHandlerFn) {
		// check if this handler is already registered
		for (const handler of this.#handlers) {
			if (handler.event_name === event_name && handler.handler_fn === handler_fn) {
				return
			}
		}

		this.#handlers.push({ event_name, handler_fn })
	}

	#_unsubscribe(event_name: string, handler_fn: EventHandlerFn) {
		this.#handlers = this.#handlers.filter(
			(handler) => handler.event_name !== event_name || handler.handler_fn !== handler_fn,
		)
	}

	emit<TEventName extends keyof TEvents & string>(event_name: TEventName, values: TEvents[TEventName]) {
		this.#_emit(event_name, values)
	}

	subscribe<TEventName extends keyof TEvents & string>(
		event_name: TEventName,
		handler: (values: TEvents[TEventName]) => void,
	) {
		this.#_subscribe(event_name, handler)
	}

	unsubscribe<TEventName extends keyof TEvents & string>(
		event_name: TEventName,
		handler: (values: TEvents[TEventName]) => void,
	) {
		this.#_unsubscribe(event_name, handler)
	}
}

export default TypedEventEmitter
